import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Dialog, DialogActions, DialogContent, useTheme } from '@mui/material';
import {
  MarketButton,
  MarketHeader,
  MarketList,
  MarketRow,
} from '@market/react';
import { useCheckoutFlow } from 'components/context/checkout_flow';
import { useOrderDetails } from 'components/context/order_details';
import ControlledInputText from 'components/common/forms/controlled_input_text';
import AddressSummary from 'components/common/summary/address_summary';
import { POSTAL_CODE_REGEX_BY_COUNTRY } from 'constants/patterns';

const POSTAL_CODE = 'postal_code';

const AccessPointSelection = (props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { store } = useOrderDetails();
  const { checkoutClient } = useCheckoutFlow();

  const {
    control,
    setError,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    reValidateMode: 'onSubmit',
  });

  const [selectedAccessPoint, setSelectedAccessPoint] = useState(null);
  const [accessPoints, setAccessPoints] = useState([]);

  const { open, onAddressSelect, handleClose } = props;

  const onClose = () => {
    handleClose();
    setAccessPoints([]);
    setSelectedAccessPoint(null);
  };

  const onSubmitPostalCode = (data) => {
    setSelectedAccessPoint(null);
    return checkoutClient
      .listAccessPoints(data[POSTAL_CODE])
      .then((resp) => {
        const validAccessPoints = resp.filter((ap) => ap.address_line_1);
        validAccessPoints.length > 0
          ? setAccessPoints(validAccessPoints)
          : setError(POSTAL_CODE, {
              type: 'manual',
              message: t('accessPointsModal.noneFound'),
            });
      })
      .catch(() =>
        setError(POSTAL_CODE, {
          type: 'manual',
          message: t('error.api.generic'),
        }),
      );
  };

  const searchForm = () => (
    <DialogContent sx={{ px: 4, py: 1, overflowY: 'unset' }}>
      <form id="access-point-form" onSubmit={handleSubmit(onSubmitPostalCode)}>
        <ControlledInputText
          shouldUnregister
          name={POSTAL_CODE}
          control={control}
          rules={{
            required: t('error.validation.invalidPostalCode'),
            pattern: {
              value: new RegExp(POSTAL_CODE_REGEX_BY_COUNTRY[store], 'i'),
              message: t('error.validation.invalidPostalCode'),
            },
          }}
          label={t('checkout.label.postalCode')}
        />
        <MarketButton
          {...(isSubmitting ? { isLoading: true } : {})}
          id="access-point-btn"
          rank="primary"
          type="submit"
          style={{ margin: theme.spacing(2, 0), width: '100%' }}
        >
          {t('common.search')}
        </MarketButton>
      </form>
    </DialogContent>
  );

  const resultsView = () => {
    if (isSubmitting || accessPoints.length === 0) return null;
    return (
      <>
        <DialogContent sx={{ px: 4, py: 1 }}>
          <MarketList
            interactive
            onMarketListSelectionsDidChange={(e) =>
              setSelectedAccessPoint(e.detail.newSelectionValue)
            }
          >
            {accessPoints.map((ap) => (
              <MarketRow key={Object.values(ap).join('+')} value={ap}>
                {ap.address_line_3 && <b>{ap.address_line_3}</b>}
                <AddressSummary address={ap} />
              </MarketRow>
            ))}
          </MarketList>
        </DialogContent>
        <DialogActions sx={{ px: 4, pt: 1, pb: 4, overflowY: 'unset' }}>
          <MarketButton
            {...(!selectedAccessPoint && { disabled: true })}
            rank="primary"
            onClick={() => {
              onAddressSelect(selectedAccessPoint);
              onClose();
            }}
            style={{ width: '100%' }}
          >
            {t('common.continue')}
          </MarketButton>
        </DialogActions>
      </>
    );
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <MarketHeader
        compact
        showNavigation
        onMarketHeaderNavigate={onClose}
        style={{ margin: theme.spacing(4, 4, 1) }}
      >
        <h1>{t('accessPointsModal.title')}</h1>
      </MarketHeader>
      {searchForm()}
      {resultsView()}
    </Dialog>
  );
};

export default AccessPointSelection;
